import React from "react"
import { Formik, Form } from "formik"
import { ProductionStepExecutionWeightValidationSchema } from "../../../utils/yupValidators"
import { makeStyles } from "@mui/styles"
import { COLORS, roundNumber } from "../../../utils"
import { StyledErrorMessage } from "../styledComponents"
import PSEMergedWeighing from "../preview/PSEMergedWeighing"
import PSESectionOutputWrapper from "../PSESectionOuptut"
import ProductionStepExecutionCardContainer from "./ProductionStepExecutionCardContainer"
import PackagingDateWeightDistribution from "../PackagingDateWeightDistribution"
import { useSelector } from "react-redux"
import { getPSESectionSelector } from "../../../reducers/Production/ProductionSteps"
import { checkIsPSEWithMultiPackagingDate } from "../../../utils/packagingExecutionUtils"

export const productionStepExecutionWeightValidationFormId = "productionStepExecutionWeightValidationForm"

const ProductionStepExecutionWeightValidation = ({ productionStepExecution, handleSubmit, hasWeightMergedFormErrors }) => {

    const pseSection = useSelector(getPSESectionSelector)
    const classes = useStyles()
    const wheighingLabel = productionStepExecution.mergedProductionStepExecutions &&
        productionStepExecution.mergedProductionStepExecutions.length > 1 &&
        !productionStepExecution.ulteriorStep
        ? "Pesez la préparation et saisissez son poids, puis indiquez la répartition de la quantité par recette en fonction du poids final pesé."
        : "Pesez la préparation et saisissez son poids."

    // hasSectionOutputs: indicates if section outputs are required (different from merged PSE distribution)
    // For classic PSEs: check pseSection from store
    // For merged PSEs: the wrapper will fetch sections per merged entry; show if isSectionLastStep
    // Also true for the last non-TECHNICAL step whose ulteriorStep is a TECHNICAL isSectionLastStep
    // (that step acts as the proxy for the weight distribution screen since the TECHNICAL step is hidden).
    const ulteriorStep = productionStepExecution.ulteriorStep
    const isLastBeforeTechnicalSectionLastStep = (
        ulteriorStep?.isSectionLastStep
        && ulteriorStep?.productionStepSnapshot?.transformation === "TECHNICAL"
    )
    const hasSectionOutputs = (productionStepExecution.isSectionLastStep || isLastBeforeTechnicalSectionLastStep) && (
        (pseSection?.sectionOutputs && pseSection.sectionOutputs.length > 0) ||
        (productionStepExecution.isStepMerged && productionStepExecution.mergedProductionStepExecutions?.length > 0)
    )

    const isStepMerged = !!(productionStepExecution.isStepMerged || (productionStepExecution.mergedProductionStepExecutions?.length > 0))

    const packagingExecutions = productionStepExecution.packagingExecutions || []
    const { isMultiPackagingDate, distinctPackagingDates } = checkIsPSEWithMultiPackagingDate(productionStepExecution)
    const initialPackagingDateWeights = distinctPackagingDates.reduce((acc, date) => ({ ...acc, [date]: productionStepExecution.packagingDateWeights?.[date] ?? null }), {})

    const validate = (values) => {
        const errors = {}

        if (!isStepMerged) {
            // Classic PSE: validate the flat sectionOutputWeights against sectionOutputExpectedTotal
            // (which is the sum of section output ingredient weights, not the full step weight)
            const weights = values.sectionOutputWeights || {}
            if (values.hasSectionOutputs && Object.keys(weights).length === 0) {
                errors.sectionOutputWeights = "Veuillez indiquer la répartition de la quantité par sortie de section."
            } else if (Object.keys(weights).length > 0) {
                const total = roundNumber(Object.values(weights).reduce((acc, curr) => acc + Number(curr || 0), 0), 2)
                // Use sectionOutputExpectedTotal if set (ingredient-type outputs),
                // otherwise fall back to netWeight (percentage-type outputs cover the full step weight)
                const expectedTotal = roundNumber(values.netWeight || values.sectionOutputExpectedTotal, 2)
                const diff = Math.abs(total - expectedTotal)
                // Use tolerance check to avoid floating-point precision issues (e.g., 3.12 + 3.12 = 6.240000000001)
                if (diff > 0.01) {
                    if (total > expectedTotal) errors.sectionOutputWeights = "L'ensemble des quantités saisies est supérieur au poids final pesé."
                    else if (total < expectedTotal) errors.sectionOutputWeights = "L'ensemble des quantités saisies est inférieur au poids final pesé."
                }
            }
        } else {
            // Merged PSE: each entry has its own weights in sectionOutputWeightsByPSE[pseObjectId]
            // Validate each PSE block independently against its own theoreticalNetWeight
            const byPSE = values.sectionOutputWeightsByPSE || {}
            const mergedPSEs = productionStepExecution.mergedProductionStepExecutions || []
            for (const merged of mergedPSEs) {
                if (merged.ulteriorStep) continue
                const pseObjectId = merged.productionStepExecution?.objectId
                if (!pseObjectId) continue
                const weights = byPSE[pseObjectId] || {}
                if (Object.keys(weights).length === 0) continue // not yet filled in
                const total = roundNumber(Object.values(weights).reduce((acc, curr) => acc + Number(curr || 0), 0), 2)
                const expected = roundNumber(merged.theoreticalNetWeight, 2)
                const diff = Math.abs(total - expected)
                if (diff > 0.01) {
                    errors.sectionOutputWeights = `La répartition pour ${merged.uniqueCode} (${merged.recipeName}) est incorrecte : total saisi ${roundNumber(total, 2)} kg ≠ poids prévu ${expected} kg.`
                    break
                }
            }
        }
        if (isMultiPackagingDate) {
            const weights = values.packagingDateWeights || {}
            const total = Object.values(weights).reduce((sum, v) => sum + Number(v || 0), 0)
            const diff = Math.abs(roundNumber(total, 2) - Number(values.netWeight || 0))
            if (values.netWeight != null && diff > 0.01) {
                if (total > values.netWeight) errors.packagingDateWeights = "La somme des poids répartis est supérieure au poids final pesé."
                else errors.packagingDateWeights = "La somme des poids répartis est inférieure au poids final pesé."
            }
            const anyFilled = Object.values(weights).some(v => v != null)
            if (values.netWeight != null && !anyFilled) {
                errors.packagingDateWeights = "Veuillez répartir le poids entre les jours de barquettage."
            }
        }
        return errors
    }

    return (
        <Formik
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validate={validate}
            initialValues={{
                netWeight: null,
                netWeightMerged: null,
                sectionOutputWeights: {},
                sectionOutputWeightsByPSE: {},
                sectionOutputExpectedTotal: null,
                hasSectionOutputs: hasSectionOutputs,
                isStepMerged: isStepMerged,
                hasError: hasWeightMergedFormErrors,
                packagingDateWeights: initialPackagingDateWeights,
            }}
            validationSchema={ProductionStepExecutionWeightValidationSchema}>
            {({ values, setFieldValue, errors, handleChange }) => {
                return (
                    <Form id={productionStepExecutionWeightValidationFormId}>
                        <div className={classes.container}>
                            <div className={classes.mainContainer}>
                                <div>
                                    <div className={classes.titleContainer}>
                                        <div className={classes.iconBox}>
                                            <img
                                                src={"/img/recipes/weight.svg"}
                                                alt="weight"
                                                className={classes.icon}
                                            />
                                        </div>
                                        <div className={classes.titleWrapper}>
                                            <h3 className={classes.title}>Poids</h3>
                                            <p className={classes.subtitle}>{wheighingLabel}</p>
                                        </div>
                                    </div>
                                    <div className={classes.weightsContainer}>
                                        <div className={classes.weightColumn}>
                                            <label className={classes.label}>Poids final prévu</label>
                                            <div className={classes.weightValue}>
                                                {roundNumber((productionStepExecution.theoreticalNetWeight || 0), 2)}
                                            </div>
                                            <span className={classes.unitLabel}>kg</span>
                                        </div>
                                        <div className={classes.divider}></div>
                                        <div className={classes.weightColumn}>
                                            <label className={classes.label} htmlFor="netWeight">
                                                Poids final pesé
                                            </label>
                                            <div className={classes.inputWrapper}>
                                                <input
                                                    id="netWeight"
                                                    name="netWeight"
                                                    type="number"
                                                    inputMode="decimal"
                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                    step="any"
                                                    className={`${classes.input} ${errors.netWeight ? classes.inputError : ""}`}
                                                    value={values.netWeight ?? ""}
                                                    onChange={handleChange}
                                                    placeholder="0"
                                                    onWheel={(e) => e.target?.blur()}
                                                />
                                            </div>
                                            <span className={classes.unitLabel}>kg</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {errors.netWeight && (
                                <StyledErrorMessage>{errors.netWeight}</StyledErrorMessage>
                            )}
                        </div>
                        {productionStepExecution.isStepMerged && !productionStepExecution.ulteriorStep &&
                            <PSEMergedWeighing
                                productionStepExecution={productionStepExecution}
                                setFieldValue={setFieldValue}
                                hasWeightMergedFormErrors={(errors && Object.keys(errors).length > 0) || hasWeightMergedFormErrors}
                                values={values}
                            />
                        }
                        {(productionStepExecution.isSectionLastStep || isLastBeforeTechnicalSectionLastStep) && hasSectionOutputs &&
                            <ProductionStepExecutionCardContainer
                                title="Répartition des poids par partie"
                                subtitle="Pesez la préparation et saisissez son poids."
                                icon="/img/recipes/balance.svg"
                                error={errors.sectionOutputWeights}
                            >
                                <PSESectionOutputWrapper
                                    productionStepExecution={productionStepExecution}
                                    values={isStepMerged ? undefined : values}
                                    onChange={isStepMerged ? undefined : (newValue) => setFieldValue("sectionOutputWeights", newValue)}
                                    onExpectedTotalChange={isStepMerged ? undefined : (total) => setFieldValue("sectionOutputExpectedTotal", total)}
                                    sectionOutputWeightsByPSE={isStepMerged ? values.sectionOutputWeightsByPSE : undefined}
                                    onChangeMerged={isStepMerged ? (pseObjectId, newWeights) => setFieldValue("sectionOutputWeightsByPSE", {
                                        ...values.sectionOutputWeightsByPSE,
                                        [pseObjectId]: newWeights
                                    }) : undefined}
                                    error={errors.sectionOutputWeights}
                                />
                            </ProductionStepExecutionCardContainer>
                        }
                        {isMultiPackagingDate &&
                            <PackagingDateWeightDistribution
                                packagingExecutions={packagingExecutions}
                                productionDate={productionStepExecution.productionDate || productionStepExecution.stepProductionDate}
                                theoreticalNetWeight={productionStepExecution.theoreticalNetWeight}
                                netWeight={values.netWeight}
                                packagingDateWeights={values.packagingDateWeights}
                                onChange={(date, value) => setFieldValue("packagingDateWeights", {
                                    ...values.packagingDateWeights,
                                    [date]: value,
                                })}
                                error={errors.packagingDateWeights}
                            />
                        }
                    </Form>
                )
            }}
        </Formik>
    )

}

export default ProductionStepExecutionWeightValidation


const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom:"24px",
    },
    mainContainer: {
        padding: "16px",
        borderRadius: "8px",
        border: `1px solid ${COLORS.BLACK_200}`,
        backgroundColor: COLORS.WHITE
    },
    titleContainer: {
        display: "flex",
        gap: "12px",
        alignItems: "center",
        marginBottom: "24px",
    },
    iconBox: {
        padding: "14px",
        backgroundColor: COLORS.BLACK_200,
        borderRadius: "8px",
        height: "52px",
        width: "52px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    icon: {
        width: "24px",
        height: "24px"
    },
    titleWrapper: {
        display: "flex",
        flexDirection: "column"
    },
    title: {
        color: COLORS.BLACK800,
        fontSize: "20px",
        fontWeight: 500,
        lineHeight: "28px",
        marginBottom: "4px",
        marginTop: 0
    },
    subtitle: {
        color: COLORS.BLACK_700,
        fontSize: "18px",
        fontWeight: 400,
        lineHeight: "24px",
        margin: 0
    },
    weightsContainer: {
        display: "flex",
        padding: "32px 0",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "48px"
    },
    weightColumn: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    label: {
        color: COLORS.BLACK800,
        textAlign: "center",
        fontSize: "20px",
        fontWeight: 400,
        lineHeight: "20px",
        marginBottom: "16px"
    },
    weightValue: {
        fontSize: "40px",
        fontWeight: 400,
        textAlign: "center",
        color: COLORS.BLACK800,
        lineHeight: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    },
    inputWrapper: {
        position: "relative",
        width: "141px"
    },
    input: {
        fontSize: "40px",
        fontWeight: 400,
        textAlign: "center",
        color: COLORS.BLACK800,
        padding: "0",
        border: "none",
        borderBottom: `1px solid ${COLORS.BLACK_500}`,
        outline: "none",
        width: "100%",
        transition: "border-color 0.2s",
        "&:focus": { borderBottom: `1px solid ${COLORS.PRIMARY_COLOR}` },
        "&:hover": { borderBottom: `1px solid ${COLORS.PRIMARY_COLOR}` },
        "&::placeholder": { color: COLORS.BLACK_300 },
    },
    inputError: {
        borderBottom: `2px solid ${COLORS.ERROR}`
    },
    unitLabel: {
        color: COLORS.BLACK_500,
        textAlign: "center",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "16px",
        marginTop: "16px"
    },
    divider: {
        width: "1px",
        backgroundColor: COLORS.BACKGROUND_LIGHT_GREY,
        alignSelf: "stretch"
    }
})